import Env from "@/constants/environment"
import * as Sentry from "@sentry/vue"

type ErrMsg<T extends Error = Error> = T | string
type Context<T extends Record<string, any> = Record<string, any>> = T

interface Logger {
  debug(msg: ErrMsg, context?: Context): void
  error(msg: ErrMsg, context?: Context): void
  info(msg: ErrMsg, context?: Context): void
  warn(msg: ErrMsg, context?: Context): void
}

type Level = "debug" | "info" | "warn" | "error"
const sentryLevel: Record<Level, Sentry.SeverityLevel> = {
  debug: "debug",
  info: "info",
  warn: "warning",
  error: "error",
}

const log = (msg: ErrMsg, level: Level, context?: Context) => {
  const error = typeof msg === "string" ? new Error(msg) : msg

  // NOTE(spk): the browser ignores this stack manipulation in the console trace.
  if (error.stack) {
    const stack = error.stack.split("\n")
    if (stack.length > 3) {
      stack.splice(1, 2) // NOTE(spk): Remove logger entries from trace
      error.stack = stack.join("\n")
    }
  }

  Sentry.captureException(error, {
    extra: context,
    level: sentryLevel[level],
  })

  if (Env.isDevelopment) {
    console[level](`[${level.toUpperCase()}]: ${error.message}`, context)
  }
}

export const logger: Logger = {
  debug: (msg: ErrMsg, context?: Context) => log(msg, "debug", context),
  error: (msg: ErrMsg, context?: Context) => log(msg, "error", context),
  info: (msg: ErrMsg, context?: Context) => log(msg, "info", context),
  warn: (msg: ErrMsg, context?: Context) => log(msg, "warn", context),
}

export default logger
